import {
	type FileConfigFieldError,
	type ImportDefinitionVersionDTO,
	type ImportDesignerDryRun,
	ImportDesignerErrorCode,
	type ImportDesignerPreview,
	type ImportDesignerSample,
	type TenantConfigPlane,
	ValidationErrorCode,
} from '@today/types'
import { isAxiosError } from 'axios'
import { useState } from 'react'
import {
	applyInspection,
	contractOf,
	type DesignerState,
	missingProcessing,
} from '@/modules/tenant-config/sections/import-definitions/designer-state'
import { importDesignerService } from '@/services/import-designer.service'
import { type ImportDefinitionSaveError, type SaveRequest, saveErrorOf } from './useImportDefinitions'

/** Why an inspect, preview or dry-run was refused, in the terms the designer shows it. */
export type DesignerCallError =
	| { kind: 'invalid'; fields: FileConfigFieldError[] }
	| { kind: 'no-importer' }
	| { kind: 'failed' }

interface ApiErrorBody {
	error?: { code?: string; details?: { fields?: FileConfigFieldError[] } }
}

const callErrorOf = (error: unknown): DesignerCallError => {
	const body = isAxiosError<ApiErrorBody>(error) ? error.response?.data?.error : undefined
	if (
		body?.code === ValidationErrorCode.IMPORT_DEFINITION_INVALID ||
		body?.code === ImportDesignerErrorCode.REQUEST_INVALID
	)
		return { kind: 'invalid', fields: body.details?.fields ?? [] }
	if (body?.code === ImportDesignerErrorCode.NO_IMPORTER) return { kind: 'no-importer' }
	return { kind: 'failed' }
}

/** A chosen file as a designer sample: its name and its bytes as base64. */
export const readSample = (file: File): Promise<ImportDesignerSample> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => {
			const url = String(reader.result)
			resolve({ filename: file.name, contentBase64: url.slice(url.indexOf(',') + 1) })
		}
		reader.onerror = () => reject(reader.error)
		reader.readAsDataURL(file)
	})

/** Save and activate, from {@link useImportDefinitions} (the F5 routes). */
interface DesignerDeps {
	save: (request: SaveRequest) => Promise<ImportDefinitionVersionDTO>
	activate: (key: string, version: number) => Promise<ImportDefinitionVersionDTO>
}

type SetState = (fn: (state: DesignerState) => DesignerState) => void

/** Runs one call with the busy flag held, reporting a failure instead of throwing. */
const useRunner = () => {
	const [busy, setBusy] = useState(false)
	const run = async <T>(fn: () => Promise<T>, onError: (error: unknown) => void): Promise<T | undefined> => {
		setBusy(true)
		try {
			return await fn()
		} catch (error) {
			onError(error)
			return undefined
		} finally {
			setBusy(false)
		}
	}
	return { busy, run }
}

type Run = ReturnType<typeof useRunner>['run']

/** Inspect, preview and dry-run: the real engine, nothing stored (BR-8, BR-9). */
const useEngineCalls = (
	tenantId: string,
	plane: TenantConfigPlane,
	state: DesignerState,
	setState: SetState,
	run: Run,
) => {
	const [preview, setPreview] = useState<ImportDesignerPreview | null>(null)
	const [dryRun, setDryRun] = useState<ImportDesignerDryRun | null>(null)
	const [callError, setCallError] = useState<DesignerCallError | null>(null)
	const onError = (error: unknown) => setCallError(callErrorOf(error))
	const withSample = async <T>(call: (sample: ImportDesignerSample) => Promise<T>, done: (result: T) => void) => {
		const { sample } = state
		if (!sample) return
		setCallError(null)
		const result = await run(() => call(sample), onError)
		if (result) done(result)
	}
	const upload = async (file: File) => {
		setCallError(null)
		const sample = await readSample(file)
		const inspection = await run(() => importDesignerService.inspect(tenantId, plane, sample), onError)
		if (inspection) setState((s) => applyInspection(s, sample, inspection))
	}
	const contract = contractOf(state)
	return {
		preview,
		dryRun,
		callError,
		upload,
		runPreview: () =>
			withSample((sample) => importDesignerService.preview(tenantId, plane, contract, sample), setPreview),
		runDryRun: () => withSample((sample) => importDesignerService.dryRun(tenantId, plane, contract, sample), setDryRun),
	}
}

/** Save and activate through the F5 routes (BR-12); the stored version becomes the designer's base. */
const useSaving = (state: DesignerState, setState: SetState, run: Run, deps: DesignerDeps) => {
	const [saveError, setSaveError] = useState<ImportDefinitionSaveError | null>(null)
	const onError = (error: unknown) => setSaveError(saveErrorOf(error))
	const rebase = (v: ImportDefinitionVersionDTO) =>
		setState((s) => ({ ...s, base: { version: v.version, status: v.status, revision: v.revision } }))
	const save = async () => {
		setSaveError(null)
		const saved = await run(() => deps.save({ key: state.key, contract: contractOf(state), base: state.base }), onError)
		if (saved) rebase(saved)
	}
	const activate = async () => {
		const { base } = state
		if (!base) return
		setSaveError(null)
		const done = await run(() => deps.activate(state.key, base.version), onError)
		if (done) rebase(done)
	}
	return { saveError, save, activate }
}

/**
 * The designer's state and its calls (TO-400 design D7): the sample is read in
 * the browser and sent base64 with each call; inspect, preview and dry-run run
 * the real engine and store nothing; save and activate go through the F5
 * routes (BR-12). Preview, dry-run and save wait for a complete keyed strategy.
 */
export function useImportDesigner(
	tenantId: string,
	plane: TenantConfigPlane,
	initial: DesignerState,
	deps: DesignerDeps,
) {
	const [state, setState] = useState<DesignerState>(initial)
	const { busy, run } = useRunner()
	const engine = useEngineCalls(tenantId, plane, state, setState, run)
	const saving = useSaving(state, setState, run, deps)
	const missing = missingProcessing(state)
	return {
		state,
		update: setState,
		contract: contractOf(state),
		missing,
		ready: missing.length === 0 && state.sample !== null,
		busy,
		...engine,
		...saving,
	}
}
